import React, { useEffect, forwardRef, useContext } from 'react';
import styled, { ThemeContext } from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { position } from 'styled-system';
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock';
import { Portal } from '../portal/Portal';
import { ModalHeader, ModalBody, ModalFooter } from '../modal/Modal';
import { Icon } from '../icon/Icon';
import { Button } from '../button/Button';
import { Box, Flex } from '../grid/Grid';

const placementStyleMap = {
  top: { top: 0, left: 0, right: 0, height: 231 },
  bottom: { bottom: 0, left: 0, right: 0, height: 231 },
  right: { right: 0, top: 0, bottom: 0, width: 400 },
  left: { left: 0, top: 0, bottom: 0, width: 400 },
};

const Mask = styled(Box)`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: ${themeGet('colors.blacks.5')};
  z-index: ${themeGet('zIndex.modalMask')};
`;

const CloseButton = styled(Button).attrs({
  variant: 'link',
})`
  position: absolute;
  top: ${themeGet('drawer.closeButton.top')};
  right: ${themeGet('drawer.closeButton.right')};
  width: ${themeGet('drawer.closeButton.size')};
  height: ${themeGet('drawer.closeButton.size')};
  background-color: ${themeGet('drawer.closeButton.backgroundColor')};
  border-radius: ${themeGet('drawer.closeButton.borderRadius')};
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  cursor: pointer;
  z-index: ${themeGet('zIndex.fixed')};
`;

const CloseButtonIcon = styled(Icon).attrs({
  name: 'CLOSE',
  color: 'blacks.3',
})`
  width: ${themeGet('drawer.closeButton.size')};
  height: ${themeGet('drawer.closeButton.size')};
  color: ${themeGet('drawer.closeButton.color')};
`;

const Content = styled(Flex)`
  position: absolute;
  flex-direction: column;
  z-index: ${themeGet('zIndex.modal')};
  box-shadow: ${themeGet('drawer.content.boxShadow')};
  background-color: ${themeGet('drawer.content.backgroundColor')};
  border: ${themeGet('drawer.content.border')};
  border-radius: ${themeGet('drawer.content.borderRadius')};
  border-color: ${themeGet('drawer.content.borderColor')};
  ${position}
`;

export const Drawer = forwardRef(function Drawer(
  { children, show, closeButton, onClose, maskClosable = true, placement = 'right', ...props },
  ref
) {
  useEffect(() => {
    show ? disableBodyScroll() : clearAllBodyScrollLocks();
  }, [show]);
  return (
    show && (
      <Portal>
        <Mask onClick={maskClosable && onClose}>
          <Content
            {...placementStyleMap[placement]}
            {...props}
            ref={ref}
            onClick={event => event.stopPropagation()}
          >
            {React.isValidElement(closeButton)
              ? closeButton
              : closeButton && (
                  <CloseButton variant="link" onClick={onClose}>
                    <CloseButtonIcon />
                  </CloseButton>
                )}
            {children}
          </Content>
        </Mask>
      </Portal>
    )
  );
});

export const DrawerHeader = forwardRef(function DrawerHeader({ ...props }, ref) {
  const {
    drawer: { header: defaultHeaderStyle },
  } = useContext(ThemeContext);
  const {
    heading: { fontSize: defaultFontSize },
  } = defaultHeaderStyle;
  return <ModalHeader ref={ref} fontSize={defaultFontSize} {...defaultHeaderStyle} {...props} />;
});

export const DrawerBody = forwardRef(function DrawerBody({ ...props }, ref) {
  const {
    drawer: { body: defaultBodyStyle },
  } = useContext(ThemeContext);
  return <ModalBody ref={ref} flex={1} overflowY="auto" {...defaultBodyStyle} {...props} />;
});

export const DrawerFooter = forwardRef(function DrawerFooter({ ...props }, ref) {
  const {
    drawer: { footer: defaultFooterStyle },
  } = useContext(ThemeContext);
  return <ModalFooter ref={ref} {...defaultFooterStyle} {...props} />;
});
